import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { restCacheConfig, restClient } from '../config/axios';
import { QUERY_KEYS } from '../hooks/hooksUtils';
import { useInfiniteQueryData } from '../hooks/useInfiniteQueryData';
import { useQueryData } from '../hooks/useQueryData';
import { useUserStore } from '../stores/UserStore';
import { ACTION_TYPES } from '../types/postsTypings';
import { PROJECT_STATUS } from '../types/projectTypings';
import { RestService } from './RestService';
import { extendFormData, MULTIPART_HEADERS } from './Utils';

export const fetchProjects = ({ pageParam, queryKey }) => {
  return RestService.get(`/projects?type=${queryKey[1].toUpperCase()}&page=${pageParam}`);
};

export const useProjectsData = (type: string) => {
  return useInfiniteQueryData([QUERY_KEYS.PROJECTS, type.toLowerCase()], fetchProjects, restCacheConfig);
};

export const fetchProject = ({ queryKey }: any) => {
  return RestService.get(`/projects/` + queryKey[1]);
};

export const useProjectData = (uuid: string) => {
  return useQueryData([QUERY_KEYS.PROJECTS, uuid], fetchProject, restCacheConfig);
};

export const fetchTasksQuery = (projectUuid: string, targetUuid: string) => {
  return restClient.fetchQuery({ queryKey: [QUERY_KEYS.TASKS, projectUuid, targetUuid], queryFn: fetchTasks, ...restCacheConfig });
};

export const fetchTasks = ({ queryKey }: any) => {
  return RestService.get(`/tasks/${queryKey[1]}?target=${queryKey[2]}`);
};

export const useTasksData = (uuid: string, target: string) => {
  return useQueryData([QUERY_KEYS.TASKS, uuid, target], fetchTasks, restCacheConfig);
};

export const fetchTask = ({ queryKey }: any) => {
  return RestService.get(`/tasks/info/${queryKey[1]}`);
};

export const useTaskData = (uuid: string) => {
  return useQueryData([QUERY_KEYS.TASKS, uuid], fetchTask, { ...restCacheConfig, enabled: !!uuid });
};

export const fetchProjectUsers = ({ queryKey }: any) => {
  return RestService.get(`/projects/users/` + queryKey[1]);
};

export const useProjectUsersData = (uuid: string) => {
  return useQueryData([QUERY_KEYS.PROJECT_USERS, uuid], fetchProjectUsers, restCacheConfig);
};

const fetchTags = () => {
  return RestService.get(`/projects/tags`);
};

export const useProjectsTagsData = () => {
  return useQueryData([QUERY_KEYS.PROJECT_TAGS], fetchTags, restCacheConfig);
};

const onSuccessAddProjectsData = async (queryClient: any, queryKey: any, newData: any) => {
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!!oldData) {
    queryClient.setQueryData(queryKey, (oldQueryData: any) => {
      return {
        ...oldQueryData,
        pages: oldQueryData.pages.map((pageData: any, index: number) => {
          return {
            ...pageData,
            data: index === 0 ? [newData.data, ...pageData.data] : pageData.data
          };
        })
      };
    });
  }
};

export const useAddProjectsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ project, formData }: any) => {
      const userUuid = useUserStore.getState().UUID;
      const businessUuid = useUserStore.getState().UBUUID;
      const userData = {
        userUuid,
        businessUuid,
        uuid: uuidv4()
      };
      if (formData) {
        extendFormData(formData, userData);
        return RestService.post('/projects', formData);
      } else {
        const data = _.assign(project, userData);
        return RestService.post('/projects', data);
      }
    },
    onSuccess: (newData: any) => {
      onSuccessAddProjectsData(queryClient, [QUERY_KEYS.PROJECTS, 'active'], newData);
      queryClient.setQueryData([QUERY_KEYS.PROJECT_TAGS], (oldQueryData: any) => {
        return {
          ...oldQueryData,
          data: [
            ..._.union(
              oldQueryData.data,
              newData.data.tags.map(tag => tag.name)
            )
          ]
        };
      });
    }
  });
};

const onSuccessUpdateProjectsData = async (queryClient: any, queryKey: any, newData: any) => {
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!oldData) {
    queryClient.setQueryData(queryKey, (oldQueryData: any) => {
      if (!oldQueryData.pages) {
        oldQueryData.data = newData.data;
        return oldQueryData;
      }
      return {
        ...oldQueryData,
        pages: oldQueryData.pages.map((pageData: any, index: number) => {
          return {
            ...pageData,
            data: pageData.data.map(item => (item.uuid === newData.data.uuid ? newData.data : item))
          };
        })
      };
    });
  }
};

export const useUpdateProjectsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ project, formData }: any) => {
      return RestService.update('/projects/' + project.uuid, formData || project, formData && MULTIPART_HEADERS);
    },
    onSuccess: (newData: any) => {
      onSuccessUpdateProjectsData(queryClient, [QUERY_KEYS.PROJECTS, newData.data.status.toLowerCase()], newData);
      onSuccessUpdateProjectsData(queryClient, [QUERY_KEYS.PROJECTS, newData.data.uuid], newData);

      queryClient.setQueryData([QUERY_KEYS.PROJECT_TAGS], (oldQueryData: any) => {
        return {
          ...oldQueryData,
          data: [
            ..._.union(
              oldQueryData.data,
              newData.data.tags.map(tag => tag.name)
            )
          ]
        };
      });
    }
  });
};

export const useArchiveProjectsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => {
      return RestService.update('/projects/' + data.uuid, data);
    },
    onSuccess: async (newData: any, data: any) => {
      const statusInverted = data.status === PROJECT_STATUS.ACTIVE ? PROJECT_STATUS.ARCHIVE : PROJECT_STATUS.ACTIVE;
      const queryKey = [QUERY_KEYS.PROJECTS, data.status.toLowerCase()];
      const queryKeyInverted = [QUERY_KEYS.PROJECTS, statusInverted.toLowerCase()];
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKeyInverted, (oldQueryData: any) => {
        return {
          ...oldQueryData,
          pages: oldQueryData.pages.map((pageData: any) => {
            return {
              ...pageData,
              data: pageData.data.filter(item => item.uuid !== newData.data.uuid)
            };
          })
        };
      });
      const oldData = queryClient.getQueryData(queryKey);
      if (!oldData) return;
      queryClient.setQueryData(queryKey, (oldQueryData: any) => {
        const newItem = { ...data, status: statusInverted };
        return {
          ...oldQueryData,
          pages: oldQueryData.pages.map((pageData: any, index: number) => {
            return {
              ...pageData,
              data: index === 0 ? [newItem, ...pageData.data] : pageData.data
            };
          })
        };
      });
    }
  });
};

const onSuccessDeleteProject = async (queryClient: any, uuid: string) => {
  const queryKey1 = [QUERY_KEYS.PROJECTS, 'active'];
  const queryKey2 = [QUERY_KEYS.PROJECTS, 'archive'];
  await queryClient.cancelQueries({ queryKey: queryKey1 });
  await queryClient.cancelQueries({ queryKey: queryKey2 });
  const oldData1 = queryClient.getQueryData(queryKey1);
  if (!!oldData1) {
    queryClient.setQueryData(queryKey1, (oldQueryData: any) => {
      return {
        ...oldQueryData,
        pages: oldQueryData.pages.map((pageData: any) => {
          return {
            ...pageData,
            data: pageData.data.filter(item => item.uuid !== uuid)
          };
        })
      };
    });
  }
  const oldData2 = queryClient.getQueryData(queryKey2);
  if (!!oldData2) {
    queryClient.setQueryData(queryKey2, (oldQueryData: any) => {
      return {
        ...oldQueryData,
        pages: oldQueryData?.pages.map((pageData: any) => {
          return {
            ...pageData,
            data: pageData.data.filter(item => item.uuid !== uuid)
          };
        })
      };
    });
  }
};

export const useDeleteProjectsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => RestService.delete('/projects/' + uuid),
    onSuccess: (newData: any, uuid: any) => onSuccessDeleteProject(queryClient, uuid)
  });
};

export const useLeaveProjectsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => RestService.delete('/projects/leave/' + uuid),
    onSuccess: (newData: any, uuid: any) => onSuccessDeleteProject(queryClient, uuid)
  });
};

export const onAddTaskMutationSuccess = (queryClient: any, newData: any) => {
  const queryKey = [QUERY_KEYS.TASKS, newData.data.projectUuid, newData.data.targetUuid];
  const oldData = queryClient.getQueryData(queryKey);
  if (!!oldData) {
    const newTask = {
      ..._.pick(newData.data, ['uuid', 'id', 'title', 'type', 'due', 'state', 'user']),
      tasks: newData.data.tasks.length,
      comments: newData.data.comments.length
    };
    queryClient.setQueryData(queryKey, (oldQueryData: any) => {
      return {
        ...oldQueryData,
        data: [newTask, ...oldQueryData.data]
      };
    });
  }
};

export const useAddTasksMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (task: any) => {
      const userUuid = useUserStore.getState().UUID;
      const businessUuid = useUserStore.getState().UBUUID;
      const data = _.assign(task, {
        userUuid,
        businessUuid,
        uuid: uuidv4()
      });
      return RestService.post('/tasks', data);
    },
    onSuccess: async (newData: any) => onAddTaskMutationSuccess(queryClient, newData)
  });
};

export const onUpdateTasksMutationSuccess = (queryClient: any, newData: any) => {
  const queryKey = [QUERY_KEYS.TASKS, newData.data.uuid];
  const oldData = queryClient.getQueryData(queryKey);
  if (!!oldData) {
    queryClient.setQueryData(queryKey, (oldQueryData: any) => {
      return {
        ...oldQueryData,
        data: newData.data
      };
    });
  }
};

export const onMutateUpdateTasksData = async (queryClient: any, data: any) => {
  const newData = {
    ..._.pick(data, ['uuid', 'id', 'title', 'type', 'due', 'state', 'user']),
    tasks: _.isArray(data.tasks) ? data.tasks.length : data.tasks,
    comments: _.isArray(data.comments) ? data.comments.length : data.comments
  };
  if (data.targetUuidOld || data.targetUuid === data.targetUuidOld) {
    const queryKey = [QUERY_KEYS.TASKS, data.projectUuid, data.targetUuid];
    await queryClient.cancelQueries({ queryKey });
    const oldData = queryClient.getQueryData(queryKey);
    if (!oldData) return;
    queryClient.setQueryData(queryKey, (oldQueryData: any) => {
      return {
        ...oldQueryData,
        data: oldQueryData.data.map(item => {
          if (item.uuid === data.uuid) {
            const users = queryClient.getQueryData([QUERY_KEYS.PROJECT_USERS, data.projectUuid]);
            if (users?.data?.length) {
              const user = users?.data?.find((user: any) => user.userUuid === data.frndUuid);
              return { ...item, ...newData, user };
            } else {
              return { ...item, ...newData };
            }
          } else {
            return { ...item };
          }
        })
      };
    });
    return { oldData };
  } else {
    const queryKey = [QUERY_KEYS.TASKS, data.projectUuid, data.targetUuid];
    const queryKeyOld = [QUERY_KEYS.TASKS, data.projectUuid, data.targetUuidOld];
    const oldData1 = queryClient.getQueryData(queryKeyOld);
    if (!!oldData1) {
      queryClient.setQueryData(queryKeyOld, (oldQueryData: any) => {
        return {
          ...oldQueryData,
          data: oldQueryData.data.filter(item => item.uuid !== data.uuid)
        };
      });
    }
    const oldData = queryClient.getQueryData(queryKey);
    if (!!oldData) {
      queryClient.setQueryData(queryKey, (oldQueryData: any) => {
        return {
          ...oldQueryData,
          data: oldQueryData.data.map(item => {
            if (item.uuid === data.uuid) {
              const users = queryClient.getQueryData([QUERY_KEYS.PROJECT_USERS, data.projectUuid]);
              if (users?.data?.length) {
                const user = users?.data?.find((user: any) => user.userUuid === data.frndUuid);
                return { ...item, ...newData, user };
              } else {
                return { ...item, ...newData };
              }
            } else {
              return { ...item };
            }
          })
        };
      });
    }
    return { oldData };
  }
};

export const useUpdateTasksMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => {
      return RestService.update('/tasks/' + data.uuid, data);
    },
    onMutate: (data: any) => onMutateUpdateTasksData(queryClient, data),
    onSuccess: async (newData: any) => onUpdateTasksMutationSuccess(queryClient, newData),
    onError: (err, data: any, context: any) => {
      queryClient.setQueryData([QUERY_KEYS.TASKS, data.projectUuid, data.targetUuid], context.oldData);
    }
  });
};

export const onDeleteTaskMutationSuccess = (queryClient: any, newData: any) => {
  const queryKey = [QUERY_KEYS.TASKS, newData.data.projectUuid, newData.data.targetUuid];
  const oldData = queryClient.getQueryData(queryKey);
  if (!!oldData) {
    queryClient.setQueryData(queryKey, (oldQueryData: any) => {
      return {
        ...oldQueryData,
        data: oldQueryData.data.filter(item => item.uuid !== newData.data.uuid)
      };
    });
  }
  const queryKey1 = [QUERY_KEYS.TASKS, newData.data.uuid];
  const oldData1 = queryClient.getQueryData(queryKey1);
  if (!!oldData1) {
    queryClient.removeQueries(queryKey1);
  }
};

export const useDeleteTasksMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => RestService.delete('/tasks/' + uuid),
    onSuccess: async (newData: any) => onDeleteTaskMutationSuccess(queryClient, newData)
  });
};

export const useDeleteUsersMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => RestService.delete('/projects/actions/' + uuid),
    onSuccess: async (newData: any, uuid) => {
      const queryKey = [QUERY_KEYS.PROJECT_USERS, newData.data.projectUuid];
      queryClient.setQueryData(queryKey, (oldQueryData: any) => {
        return {
          ...oldQueryData,
          data: oldQueryData.data.filter(item => item.uuid !== uuid)
        };
      });
    }
  });
};

export const useDeleteCommentsMutation = targetUuid => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => RestService.delete('/projects/actions/' + uuid),
    onSuccess: async (newData: any, uuid) => {
      const queryKey = [QUERY_KEYS.TASKS, newData.data.taskUuid];
      const oldData = queryClient.getQueryData(queryKey);
      if (!!oldData) {
        queryClient.setQueryData(queryKey, (oldQueryData: any) => {
          return {
            ...oldQueryData,
            data: oldQueryData.data.filter(item => item.uuid !== uuid)
          };
        });
      }
      const queryKey1 = [QUERY_KEYS.TASKS, newData.data.projectUuid, targetUuid];
      const oldData1 = queryClient.getQueryData(queryKey1);
      if (!!oldData1) {
        queryClient.setQueryData(queryKey1, (oldQueryData: any) => {
          return {
            ...oldQueryData,
            data: oldQueryData.data.map(item => {
              return {
                ...item,
                comments: item.uuid === newData.data.taskUuid ? --item.comments : item.comments
              };
            })
          };
        });
      }
    }
  });
};

export const useAddProjectCommentMutation = targetUuid => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => {
      const userUuid = useUserStore.getState().UUID;
      const businessUuid = useUserStore.getState().UBUUID;
      const action = Object.assign({}, data, {
        userUuid,
        businessUuid,
        uuid: uuidv4(),
        type: ACTION_TYPES.PROJECT_COMMENT
      });
      return RestService.post('/actions', action);
    },
    onSuccess: (newData: any) => {
      const comment = { ...newData.data };
      const queryKey = [QUERY_KEYS.TASKS, comment.taskUuid];
      const oldData = queryClient.getQueryData(queryKey);
      if (!!oldData) {
        comment.user = {
          uuid: useUserStore.getState().UUID,
          name: useUserStore.getState().UNAME,
          image: useUserStore.getState().UIMAGE
        };
        queryClient.setQueryData(queryKey, (oldQueryData: any) => {
          return {
            ...oldQueryData,
            data: {
              ...oldQueryData.data,
              comments: [comment, ...oldQueryData.data.comments]
            }
          };
        });
      }
      const queryKey1 = [QUERY_KEYS.TASKS, comment.projectUuid, targetUuid];
      const oldData1 = queryClient.getQueryData(queryKey1);
      if (!!oldData1) {
        queryClient.setQueryData(queryKey1, (oldQueryData: any) => {
          return {
            ...oldQueryData,
            data: oldQueryData.data.map(item => {
              return {
                ...item,
                comments: item.uuid === newData.data.taskUuid ? ++item.comments : item.comments
              };
            })
          };
        });
      }
    }
  });
};

export const useInviteProjectsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (action: any) => {
      const userUuid = useUserStore.getState().UUID;
      const businessUuid = useUserStore.getState().UBUUID;
      const data = _.assign(action, {
        userUuid,
        businessUuid,
        uuid: uuidv4(),
        type: ACTION_TYPES.PROJECT_INVITE
      });
      return RestService.post('/projects/invite', data);
    },
    onSuccess: (newData: any, data: any) => {
      const user = { ...newData.data };
      const oldData = queryClient.getQueryData([QUERY_KEYS.PROJECT_USERS, data.projectUuid]);
      if (!oldData) return;
      queryClient.setQueryData([QUERY_KEYS.PROJECT_USERS, data.projectUuid], (oldQueryData: any) => {
        return {
          ...oldQueryData,
          data: [user, ...oldQueryData.data]
        };
      });
    }
  });
};
